import type { ReactNode, SelectHTMLAttributes } from "react";
import type { ParameterDescriptor } from "../model/types";

export function StatusLamp({ tone = "green", children }: { tone?: "green" | "amber" | "red" | "cyan"; children: ReactNode }) {
  return <span className={`status-lamp status-lamp--${tone}`}><span aria-hidden="true" />{children}</span>;
}

function formatValue(descriptor: ParameterDescriptor, value: number) {
  if (descriptor.unit === "%") return `${Math.round(value * 100)}%`;
  if (descriptor.unit === "Hz") return value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value.toFixed(value < 10 ? 2 : 0);
  if (descriptor.unit === "s") return value < 1 ? `${Math.round(value * 1000)}ms` : `${value.toFixed(2)}s`;
  if (descriptor.unit === "°") return `${Math.round(value)}°`;
  if (descriptor.unit === "ct") return `${value > 0 ? "+" : ""}${Math.round(value)}ct`;
  if (descriptor.unit === "×") return `${value.toFixed(2)}×`;
  return `${Number(value.toFixed(2))}${descriptor.unit ? ` ${descriptor.unit}` : ""}`;
}

export function Dial({ descriptor, value, onChange, compact = false }: {
  descriptor: ParameterDescriptor;
  value: number;
  onChange: (value: number) => void;
  compact?: boolean;
}) {
  const normalized = descriptor.curve === "log"
    ? (Math.log(Math.max(value, descriptor.min)) - Math.log(descriptor.min)) / (Math.log(descriptor.max) - Math.log(descriptor.min))
    : (value - descriptor.min) / (descriptor.max - descriptor.min);
  const rangeValue = descriptor.curve === "log" ? normalized * 1000 : value;
  const min = descriptor.curve === "log" ? 0 : descriptor.min;
  const max = descriptor.curve === "log" ? 1000 : descriptor.max;
  const step = descriptor.curve === "log" ? 1 : descriptor.step;

  const change = (raw: number) => {
    if (descriptor.curve !== "log") onChange(raw);
    else onChange(Math.exp(Math.log(descriptor.min) + (raw / 1000) * (Math.log(descriptor.max) - Math.log(descriptor.min))));
  };

  return (
    <label className={`dial ${compact ? "dial--compact" : ""}`}>
      <span className="dial__label">{descriptor.shortLabel}</span>
      <span className="dial__face" aria-hidden="true">
        <svg className="dial__indicator" viewBox="0 0 56 56">
          <line x1="28" y1="28" x2="28" y2="6" transform={`rotate(${-132 + normalized * 264} 28 28)`} />
        </svg>
        <span className="dial__cap" />
      </span>
      <input
        className="dial__range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={rangeValue}
        aria-label={descriptor.label}
        aria-valuetext={formatValue(descriptor, value)}
        onChange={(event) => change(Number(event.currentTarget.value))}
        onDoubleClick={() => onChange(descriptor.defaultValue)}
      />
      <output className="dial__value">{formatValue(descriptor, value)}</output>
    </label>
  );
}

export function Toggle({ pressed, onPressedChange, children, tone = "cyan", disabled = false }: {
  pressed: boolean;
  onPressedChange: (pressed: boolean) => void;
  children: ReactNode;
  tone?: "cyan" | "amber" | "red";
  disabled?: boolean;
}) {
  return (
    <button
      className={`toggle toggle--${tone}`}
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={() => onPressedChange(!pressed)}
    >
      <span className="toggle__lamp" aria-hidden="true" />
      {children}
    </button>
  );
}

export function SelectField({ label, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; children: ReactNode }) {
  return (
    <label className="select-field">
      <span>{label}</span>
      <select {...props}>{children}</select>
    </label>
  );
}

export function Meter({ value, label, peak }: { value: number; label: string; peak?: number }) {
  const level = Math.min(100, Math.max(0, value * 100));
  const peakPosition = Math.min(100, Math.max(0, (peak ?? 0) * 100));
  return (
    <div className="meter" aria-label={`${label}: ${Math.round(value * 100)} percent`}>
      <span className="meter__label">{label}</span>
      <svg className="meter__track" viewBox="0 0 100 5" preserveAspectRatio="none" aria-hidden="true">
        <rect width={level} height="5" />
        {peak !== undefined && <line x1={peakPosition} x2={peakPosition} y1="0" y2="5" />}
      </svg>
    </div>
  );
}
